/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import Modal from "..";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import { toast } from "sonner";
import { useForgotPasswordMutation } from "@/features/auth/passwordApi";

interface VerificationLinkSentProps {
  email?: string;
  message?: string;
  onClose?: () => void;
}

const VerificationLinkSent = ({
  email,
  message,
  onClose,
}: VerificationLinkSentProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && onClose) {
      onClose();
    }
  };

  const handleResend = async () => {
    if (!email) return;

    try {
      await forgotPassword({ email }).unwrap();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to resend link");
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleOpenChange}
      classes={{ dialogContent: "sm:max-w-[496px] p-6 lg:p-8" }}
      aria-labelledby="verification-success-title"
    >
      <div className="flex flex-col items-center gap-6 text-center lg:gap-8">
        {/* Success Illustration */}
        <div className="mx-auto max-w-60">
          <Image
            src="/assets/auth/success-reset-password.svg"
            alt="Success illustration"
            width={280}
            height={200}
            className="h-auto w-full"
            priority
          />
        </div>

        {/* Content */}
        <div className="space-y-4">
          <h2
            id="verification-success-title"
            className="text-primary text-2xl font-semibold lg:text-3xl"
          >
            Check Your Email
          </h2>

          <p className="text-muted-foreground">
            {message ?? "We&apos;ve sent a password reset link to your email."}
          </p>

          {email && (
            <p className="text-muted-foreground text-sm">
              Didn&apos;t receive the email?{" "}
              <button
                type="button"
                disabled={isLoading}
                className="text-primary font-medium hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleResend}
              >
                {isLoading ? "Sending..." : "Click to resend"}
              </button>
            </p>
          )}
        </div>

        {/* Actions */}
        <Button asChild className="mt-4 w-full">
          <Link href="/login" onClick={() => setIsOpen(false)}>
            Back to Login
          </Link>
        </Button>
      </div>
    </Modal>
  );
};

export default VerificationLinkSent;
